// ---------------------------------------------------------------
//  FileMessage – renders an image or a generic file link
//  It uses the usePcakeImage hook to get a cached (blob) URL.
// ---------------------------------------------------------------
import { useEffect, useState } from 'react';
import { usePcakeImage } from '../hooks/usePcakeImage';
import { formatFileSize } from '../utils/format';

interface FileMessageProps {
  chatId: string;
  messageId: string;
  signedUrl: string;
  mimeType?: string;
  fileName: string;
  fileSize?: number;
  onClick?: () => void;
  isProfileImage?: boolean; 
}

/**
 * Guess a MIME type from the file name if we weren’t given one.
 */
function guessMime(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    mp4: 'video/mp4',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
  };
  return map[ext] ?? 'application/octet-stream';
}

export default function FileMessage({
  chatId,
  messageId,
  signedUrl,
  mimeType,
  fileName,
  fileSize,
  onClick,
  isProfileImage = false 
}: FileMessageProps) {
  // -----------------------------------------------------------------
  // 1️⃣ Build a **guaranteed** MIME string.
  // -----------------------------------------------------------------
  const safeMime = mimeType ? mimeType : guessMime(fileName);

  // -----------------------------------------------------------------
  // 2️⃣ Ask the hook for a cached URL.
  // -----------------------------------------------------------------
  const cachedUrl = usePcakeImage(chatId, messageId, signedUrl, safeMime);

  // -----------------------------------------------------------------
  // 3️⃣ Track image load errors and lightbox state
  // -----------------------------------------------------------------
  const [imageError, setImageError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [cachedUrl]);

  // -----------------------------------------------------------------
  // 4️⃣ Decide whether we are dealing with an image.
  // -----------------------------------------------------------------
  const isImage = safeMime.startsWith('image/');

  // -----------------------------------------------------------------
  // 5️⃣ Handle click
  // -----------------------------------------------------------------
  const handleClick = () => {
    if (isProfileImage && onClick) {
      onClick();
    } else if (isImage) {
      setLightboxOpen(true);
    }
  };

  // -----------------------------------------------------------------
  // 6️⃣ Render
  // -----------------------------------------------------------------
  return (
    <>
      <div className="file-message">
        {isImage && !imageError ? (
          <div
            className="image-preview"
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={cachedUrl}
              alt={fileName}
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div className="file-info-only">
            <div className="file-icon">{isImage ? '🖼️' : '📎'}</div>
            <div className="file-info">
              <a
                href={cachedUrl || signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="file-link"
              >
                {fileName}
              </a>
              {fileSize && (
                <span className="file-size">{formatFileSize(fileSize)}</span>
              )}
              {isImage && imageError && (
                <span className="file-size" style={{ color: '#fa4848' }}>
                  Image unavailable — tap to open
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox overlay for non-profile images */}
      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 20,
              color: '#fff',
              cursor: 'pointer',
              lineHeight: '36px',
              textAlign: 'center',
            }}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={cachedUrl}
            alt={fileName}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
            }}
          />
        </div>
      )}
    </>
  );
}